import { Fragment, useState } from "react"
import { GetPriceFor, Plans } from "@proj-common/pricing"
import { planFeatureAvailability } from "@proj-common/enums"
import { IPlan } from "@proj-common/types"
import { FEATURES } from "@proj-common/const"
import { Tooltip } from 'react-tooltip'

export const IncludedSign = props => <svg {...props} className="shrink-0 inline-block fill-emerald-500" xmlns="http://www.w3.org/2000/svg" width="12" height="9">
    <path d="M10.28.28 3.989 6.575 1.695 4.28A1 1 0 0 0 .28 5.695l3 3a1 1 0 0 0 1.414 0l7-7A1 1 0 0 0 10.28.28Z" />
</svg>

export const ExcludedSign = props => <svg {...props} className="shrink-0 inline-block text-rose-500" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
    <path d="M1.5 1.5 10.5 10.5M1.5 10.5 10.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
</svg>

export const OptionalSign = props => <svg {...props} className="shrink-0 inline-block text-yellow-500" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="2" fill="none" />
</svg>

export const InfoCircle = props => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
</svg>

export const getDefaultPlanLookupKey = (): string => {
    if (!Plans?.length) return ""
    let middleIndex = Math.floor(Plans.length / 2)
    let defaultPlan = Plans[middleIndex]
    let defaultPlanLookupKey = defaultPlan.paddleProductData.customData.lookup_key + "_yearly"
    return defaultPlanLookupKey
}

interface IPricingPlanOverview {
    plan: IPlan
}

const PricingPlanOverview = (props: IPricingPlanOverview) => {
    const [areDetailsVisible, setAreDetailsVisible] = useState(false)

    const toggleDetails = () => {
        setAreDetailsVisible(!areDetailsVisible)
    }

    const includedFeatures = props.plan.features.filter(f => f.availability == planFeatureAvailability.included)
    const optionalFeatures = props.plan.features.filter(f => f.availability == planFeatureAvailability.optional)

    return (
        <div className={`max-w-xs p-4 ${props.plan.details.featured ? "border-cyan-400 border-2" : "border-slate-300 border"}`}>
            <p className=" text-lg md:text-xl font-semibold uppercase mb-4 md:mb-8">{props.plan.paddleProductData.name}</p>

            <p className="md:text-lg mb-2 md:mb-4">$ <span className="text-4xl md:text-6xl font-semibold">{GetPriceFor(`${props.plan.paddleProductData.customData.lookup_key}_yearly`)}</span> USD</p>
            <p className="text-xs md:text-sm text-slate-800">Per month if billed yearly (${GetPriceFor(`${props.plan.paddleProductData.customData.lookup_key}_monthly`)} billed monthly)</p>

            <p className="mt-4 md:mt-8 text-sm md:text-lg text-slate-900 md:min-h-[180px]">{props.plan.details.small_text}</p>
            <div className="py-4 md:border-b md:border-slate-300 ">
                <a className={`${props.plan.details.featured ? "idtn-btn-primary-xl" : "idtn-btn-secondary-xl"} w-full block`} href="#TODO">
                    {props.plan.details.CTA_text}
                </a>
                <a className=" visible md:hidden w-full block mt-4 text-lg" onClick={toggleDetails}>
                    {areDetailsVisible ? "-" : "+"} See what's included
                </a>
            </div>

            <div className={` md:flex ${areDetailsVisible ? `flex` : `hidden`} flex-col space-y-4 mt-4 md:mt-8 pb-4 md:pb-8 text-sm md:text-lg text-slate-900`}>
                {includedFeatures.length > 0 && <p className="font-semibold uppercase text-black">Included in subscription:</p>}
                {
                    includedFeatures.map((feature, idx) => {
                        return <div key={`feature_${idx}`} className="flex flex-row space-x-2">
                            <div><i className="fa-solid fa-check text-emerald-600 font-bold"></i></div>
                            <div>{feature.value ? feature.value : null} {feature.label}</div>
                        </div>
                    }
                    )
                }
                {optionalFeatures.length > 0 && <p className="font-semibold uppercase text-black pt-4">Available as pay-per-use:</p>}
                {
                    optionalFeatures.map((feature, idx) => {
                        return <div key={`feature_${idx}`} className="flex flex-row space-x-2">
                            <div><i className="fa-solid fa-dollar-sign text-yellow-600  font-bold"></i></div>
                            <div>{feature.value ? feature.value : null} {feature.label}</div>
                        </div>
                    }
                    )
                }
            </div>
        </div>
    )
}

const PricingPlansOverview = props => {
    return (
        <div className="white pl-4 pr-4 pt-8 pb-8">
            <div className="flex flex-col items-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="flex flex-row justify-center flex-1 p-2">
                        <div className="flex flex-col max-w-sm text-center">
                            <h1 className="text-[1.7rem] font-bold md:text-3xl font-display leading-tight">
                                Pricing
                            </h1>
                        </div>
                    </div>
                    <div className="flex md:flex-row flex-col w-full">
                        {Plans.map((plan, idx) => {
                            return <PricingPlanOverview
                                key={`plan_overview_${idx}`}
                                plan={plan}
                            />
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

interface IComparePlanFeature {
    feature: typeof FEATURES[0]
    /**
     * `tooltip` is a hover-over tooltip from `react-tooltip` library
     * 
     * `block` is an onClick event where a new div is toggled below the feature title.
     */
    detailType: "tooltip" | "block"
}
const ComparePlanFeature = (props: IComparePlanFeature) => {
    const [showDetails, setShowDetails] = useState(false)
    return (
        <div>
            <div className="md:py-2">
                <span>{props.feature.label}</span>
                <InfoCircle className="inline cursor-pointer ml-1" data-tooltip-id={props.feature.label} onClick={() => setShowDetails(!showDetails)} />
            </div>
            {props.detailType == "tooltip" &&
                <Tooltip id={props.feature.label}
                    place="right"
                    children={<>
                        <div className="max-w-[10rem] hidden md:block">{props.feature.description}</div>
                    </>} />}
            {props.detailType == "block" && showDetails && <>
                <div className="text-xs text-slate-600 italic py-2 not-sr-only md:sr-only">{props.feature.description}</div>
            </>}
        </div>
    )
}

interface IComparePlanDetails {
    plan: IPlan
    plan_idx: number
    priceType: boolean
    overrideButtonTexts?: string
    selectedPlanCB: (priceLookupKey: string) => void
}
const ComparePlanDetails = (props: IComparePlanDetails) => {
    const [areDetailsVisible, setAreDetailsVisible] = useState(false)

    const toggleDetails = () => {
        setAreDetailsVisible(!areDetailsVisible)
    }

    return (
        <Fragment key={`plan_${props.plan_idx}`}>
            <section className="md:contents [&>div:first-child]:pt-10 [&>div:first-child]:rounded-t-2xl [&>div:last-child]:pb-10 [&>div:last-child]:rounded-b-2xl">
                <div className={`relative ${props.plan.details.featured ? "bg-indigo-50" : "bg-white"}  px-4 lg:px-6 flex flex-col justify-end`}>
                    {props.plan.details.featured && (
                        <div className="absolute top-0 right-0 mr-6 -mt-3">
                            <div className="inline-flex items-center text-xs font-semibold py-1.5 px-3 bg-indigo-600 text-white rounded-full shadow-sm shadow-slate-950/5">Recommended</div>
                        </div>
                    )}
                    <div className="grow flex flex-col">
                        <div className="text-xl flex-none font-semibold text-slate-900  mb-0.5">{props.plan.paddleProductData.name}</div>
                        <div className="text-sm flex-auto text-slate-500">{props.plan.details.small_text}</div>
                        <div className="my-3 flex-none text-center">
                            <span className="text-xl font-medium text-slate-900 ">$</span>
                            <span className="text-3xl font-bold text-slate-900 ">
                                {GetPriceFor(`${props.plan.paddleProductData.customData.lookup_key}_${props.priceType ? "yearly" : "monthly"}`)}
                            </span>
                            <span className="text-slate-500 font-medium">
                                {
                                    props.priceType ?
                                        <> USD / mo<br />(${GetPriceFor(`${props.plan.paddleProductData.customData.lookup_key}_${props.priceType ? "yearly" : "monthly"}`, false)} billed yearly)</> :
                                        <> USD / mo<br />&nbsp;</>
                                }
                            </span>
                        </div>
                    </div>
                    <div className="pb-4 md:border-b md:border-slate-300 ">
                        <div className={`${props.plan.details.featured ? "idtn-btn-primary-xl" : "idtn-btn-secondary-xl"} w-full block whitespace-nowrap`}
                            onClick={() => props.selectedPlanCB(`${props.plan.paddleProductData.customData.lookup_key}_${props.priceType ? "yearly" : "monthly"}`)}>
                            {props.overrideButtonTexts ? props.overrideButtonTexts : props.plan.details.CTA_text}
                        </div>

                        <a className="visible md:hidden w-full block mt-4 text-lg" onClick={toggleDetails}>
                            {areDetailsVisible ? "-" : "+"} See what's included
                        </a>
                    </div>
                </div>
                <div className={`${props.plan.details.featured ? "bg-indigo-50" : "bg-white"
                    }  px-4 lg:px-6 flex-col justify-end md:order-${4 + props.plan_idx + 1 > 12 ? `[${4 + props.plan_idx + 1}]` : 4 + props.plan_idx + 1
                    }  md:flex ${areDetailsVisible ? `hidden` : `hidden`}`}
                >
                    <div className="py-2 text-slate-900 font-medium mt-4 md:sr-only">Features</div>
                </div>



                {props.plan.features.map((feature, sIdx) => {
                    let sOrderIdx = 8 + props.plan_idx + 1 + sIdx * 4
                    return (
                        <Fragment key={sOrderIdx}>
                            <div
                                key={`plan_detail_${props.plan_idx}_feature_${sIdx}`}
                                className={` ${props.plan.details.featured ? "bg-indigo-50" : "bg-white"
                                    } px-4 lg:px-6  flex-col justify-end md:order-${sOrderIdx > 12 ? `[${sOrderIdx}]` : sOrderIdx
                                    } md:flex ${areDetailsVisible ? `flex` : `hidden`}`}
                            >
                                <div className="flex items-center md:place-content-center md:text-center h-full border-b border-slate-300  py-2 text-slate-600 ">
                                    <div className="w-full flex md:flex-col flex-nowrap md:items-center items-start">
                                        {feature.availability == planFeatureAvailability.included &&
                                            (feature.value ? <>
                                                <div className="flex-grow-0 mr-1 font-semibold">{feature.value}</div>
                                                <div className="md:sr-only flex-grow"><ComparePlanFeature feature={feature} detailType="block" /></div>
                                            </> : <>
                                                <div className="flex-grow-0 mr-1"><IncludedSign /></div>
                                                <div className="md:sr-only flex-grow"><ComparePlanFeature feature={feature} detailType="block" /></div>
                                            </>)}
                                        {feature.availability == planFeatureAvailability.excluded &&
                                            <>
                                                <ExcludedSign />
                                                <div className="md:sr-only flex-grow-0 mr-1"><ComparePlanFeature feature={feature} detailType="block" /></div>
                                            </>}
                                        {feature.availability == planFeatureAvailability.optional &&
                                            <div className="flex flex-nowrap md:items-center items-start place-content-around w-full">
                                                <div className="md:sr-only flex-grow-0 mr-1"><OptionalSign /></div>
                                                <div className="md:sr-only flex-grow w-full"><ComparePlanFeature feature={feature} detailType="block" /></div>
                                                <div className="flex-grow-0 italic md:not-italic text-gray-500 md:text-black font-semibold md:font-normal  text-right md:text-center">${parseInt(props.plan.paddleAddonPriceData.find(papd => papd.customData.lookup_key == feature.lookup_key).unitPrice.amount) / 100} / session</div>
                                            </div>}
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    )
                })}

                <div className={`${props.plan.details.featured ? "bg-indigo-50" : "bg-white"} 
                px-4 lg:px-6 flex-col justify-end  md:flex
                md:order-${(8 + props.plan_idx + 1 + props.plan.features.length * 4) > 12 ? `[${(8 + props.plan_idx + 1 + props.plan.features.length * 4)}]` : (8 + props.plan_idx + 1 + props.plan.features.length * 4)}`} />

            </section>
        </Fragment>
    )
}

export interface IComparePlans {
    selectedPlanCB: (priceLookupKey: string) => void
    overrideButtonTexts?: string | string[],
    hideTrialText?: boolean
    isMinimalUI?: boolean
}
export const ComparePlans = (props: IComparePlans) => {
    const [priceType, setPriceType] = useState(false)
    return (

        <div className="white pl-4 pr-4 pt-8 pb-8">
            <div className='container max-w-5xl mx-auto flex flex-col place-items-center'>

                {!props.isMinimalUI &&
                    <div className="flex flex-col items-center mb-4">
                        <div className="flex flex-row justify-center flex-1 p-2">
                            <div className="flex flex-col max-w-sm text-center">
                                <h1 className="text-[1.7rem] font-bold md:text-3xl font-display leading-tight">
                                    Compare Plans
                                </h1>
                            </div>
                        </div>
                    </div>
                }

                <div className="max-w-sm mx-auto md:max-w-none grid md:grid-cols-4 md:-mx-6 text-sm py-4 rounded-2xl bg-white">

                    {/*Column with labels*/}
                    <section className="md:contents [&>div:first-child]:pt-10 [&>div:first-child]:rounded-t-2xl [&>div:last-child]:pb-10 [&>div:last-child]:rounded-b-2xl">
                        {/*Pricing toggle*/}
                        <div className="relative bg-white  px-6 flex flex-col justify-end md:order-0">
                            <div className="pb-5 md:border-b border-slate-300 ">
                                {/*Toggle switch*/}
                                <div className="max-md:text-center">
                                    <div className="inline-flex items-center">
                                        <div className="text-sm text-slate-500 flex-1 text-right">Billed Monthly</div>
                                        <div className="relative flex flex-1">
                                            <input type="checkbox" id="toggle" className="peer sr-only" onChange={() => setPriceType(!priceType)} checked={priceType} />
                                            <label htmlFor="toggle" className="relative flex h-6 w-11 mx-auto cursor-pointer items-center rounded-full bg-slate-400 px-0.5 outline-slate-400 transition-colors before:h-5 before:w-5 before:rounded-full before:bg-white before:shadow-sm before:transition-transform before:duration-150 peer-checked:bg-rose-500 peer-checked:before:translate-x-full peer-focus-visible:outline peer-focus-visible:outline-offset-2 peer-focus-visible:outline-gray-400 peer-checked:peer-focus-visible:outline-indigo-500">
                                                <span className="sr-only">Billed Yearly</span>
                                            </label>
                                        </div>
                                        <div className="text-sm text-slate-500 flex-1">Billed Yearly</div>
                                    </div>
                                </div>
                                <div className="text-sm text-slate-500 mt-4 pt-4 border-t">{props.hideTrialText ? "" : "All plans include a 30 day trial"}</div>
                            </div>
                        </div>

                        {/*Row headers*/}
                        <div className={`bg-white px-6 flex flex-col justify-end max-md:hidden md:order-4`} aria-hidden="true">
                            <div className="py-2 text-slate-900 font-medium mt-4 text-base">Details</div>
                        </div>

                        {Object.keys(FEATURES).sort((a, b) => (FEATURES[a].id - FEATURES[b].id)).map((feature, sIdx, arr) => {
                            let cOrderIdx = 8 + sIdx * 4
                            return (
                                <Fragment key={`labels_${cOrderIdx}`}>
                                    <div className={`bg-white  px-6 flex flex-col items-start place-content-between max-md:hidden md:order-${cOrderIdx > 12 ? `[${cOrderIdx}]` : cOrderIdx}`} aria-hidden="true" >
                                        <div className="border-b border-slate-300 min-w-full">
                                            <ComparePlanFeature feature={FEATURES[feature]} key={`feature_column_${cOrderIdx}`} detailType="tooltip" />
                                        </div>
                                    </div>
                                </Fragment>
                            )
                        })}

                        <div className={`bg-white  px-6 flex flex-col items-start place-content-between max-md:hidden md:order-${8 + Object.keys(FEATURES).length * 4 > 12 ? `[${8 + Object.keys(FEATURES).length * 4}]` : 8 + Object.keys(FEATURES).length * 4}`} aria-hidden="true" />

                    </section>

                    {/*Columns with plan details*/}
                    {Plans.map((plan, pIdx) => {
                        return (
                            <ComparePlanDetails
                                key={`plan_${pIdx}`}
                                plan={plan}
                                plan_idx={pIdx}
                                priceType={priceType}
                                overrideButtonTexts={Array.isArray(props.overrideButtonTexts) ? props.overrideButtonTexts[pIdx] : props.overrideButtonTexts}
                                selectedPlanCB={props.selectedPlanCB}
                            />
                        )
                    })}
                </div>

            </div>
        </div >

    )
}